import * as React from "react";
import { useEffect, useRef, useState } from "react";
import * as ReactDOM from "react-dom";
import styled, { css } from "styled-components";
import { keypadContainerElement } from "../../core";
import { isTouchSupported } from "../../core/utils/handleEvents";
import { iPadPortraitOnly } from "../_utils/styleUtils";

type LiMouseOrTouchEvent =
  | React.MouseEvent<HTMLLIElement, MouseEvent>
  | React.TouchEvent<HTMLLIElement>;
type DivMouseOrTouchEvent =
  | React.MouseEvent<HTMLDivElement, MouseEvent>
  | React.TouchEvent<HTMLDivElement>;
type DoneCallback = (value: string) => void;

export interface INumPad {
  value: string;
  currency: string;
  onDone: DoneCallback;
  onCancel: VoidFunction;
  alt?: string;
  maxChars?: number;
  container?: Element;
}

export const DIGITS = new Map([
  ["Digit1", "1"],
  ["Digit2", "2"],
  ["Digit3", "3"],
  ["Digit4", "4"],
  ["Digit5", "5"],
  ["Digit6", "6"],
  ["Digit7", "7"],
  ["Digit8", "8"],
  ["Digit9", "9"],
  ["Digit0", "0"],
  ["Backspace", ""], // backspace
  ["Enter", ""] // enter
]);

// only used for ie11 backward compatibility
export const mapKeyToDigit = (key: string) =>
  Number.isInteger(parseInt(key, 10)) ? `Digit${key}` : key;

export const isEmpty = (v: string) => v === "";
export const hasReachLimit = (v: string, limit: number) => v.length >= limit;
export const isBackspace = (code: string) => code === "Backspace";
export const isZero = (code: string) => code === "Digit0";
export const isEnter = (code: string) => code === "Enter";
export const isAllowed = (code: string) => DIGITS.has(code);
export const isZeroFirst = (v: string, code: string) =>
  v === "" && isZero(code);
export const limitTo = (max: number) => (str: string) => str.substr(0, max);
export const add = (char: string) => (v: string) => `${v}${char}`;
export const removeLast = (v: string) => v.substring(0, v.length - 1);

export const processKey = (maxChars: number, currentValue: string) => ({
  code
}: KeyboardEventInit): string => {
  const digit = DIGITS.get(code);

  if (typeof digit !== "undefined") {
    if (!isAllowed(code) || isZeroFirst(currentValue, code)) {
      // only allowed chars + non zero as first char
      // return the current value when is invalid
      return currentValue;
    }

    // let's create a functor so is easier to apply transformations
    let ft = [currentValue];

    ft = isBackspace(code) ? ft.map(removeLast) : ft.map(add(digit));
    ft = ft.map(limitTo(maxChars));

    return ft[0]; // new value
  }

  return currentValue; // no change, return the same
};

export const keydown = (
  maxChars: number,
  setValueFn: React.Dispatch<React.SetStateAction<string>>,
  onDone: (value: string) => void,
  value: string,
  code: string
) => {
  if (isEnter(code)) {
    onDone(value);
  } else {
    const text = processKey(maxChars, value)({ code });
    setValueFn(text);
  }
};

export function NumPad({
  value: initValue,
  currency,
  onDone,
  onCancel,
  alt = "",
  maxChars = 10,
  container = keypadContainerElement.children[0]
}: INumPad) {
  const [value, setValue] = useState(initValue);
  const clear = () => setValue("");
  const clearTimer = useRef<NodeJS.Timer>();

  /**
   * startClearTimer and stopClearTimer functions are used to simulate
   * a press and hold effect in the clear button. If this button is pressed
   * for more than 600ms the entire value will be cleared,
   * otherwise it will only remove one character.
   */
  const startClearTimer = (event: LiMouseOrTouchEvent) => {
    event?.stopPropagation();
    clearTimer.current = setTimeout(clear, 600);
  };

  const stopClearTimer = (event: LiMouseOrTouchEvent) => {
    event?.stopPropagation();
    clearTimeout(clearTimer.current);
  };

  const onCloseClickHandler = () => onCancel();

  // onClickOnView is used to close the component if
  // the user tap/clicks outside the NumPad
  const onClickOnViewHandler = (event: DivMouseOrTouchEvent) => {
    event.stopPropagation();
    // let's make sure the event is coming from the main wrapper
    if (event.target === event.currentTarget) {
      onCancel();
    }
  };

  const isKeyEnabled = !hasReachLimit(value, maxChars);

  const Digit = isKeyEnabled ? DigitEnabled : DigitDisabled;
  const ClearDigit = !isEmpty(value) ? DigitEnabled : DigitDisabled;

  const onKeyDownHandler = (e: KeyboardEvent) => {
    // used in Firefox to avoid "go back" when Backspace is pressed
    (e as KeyboardEvent)?.preventDefault?.();

    const code = typeof e.code === "undefined" ? mapKeyToDigit(e.key) : e.code; // ie11 needed
    keydown(maxChars, setValue, onDone, value, code);
  };

  // Click on any digit will simulate a keydown event
  const onClickHandler = ({ code }: { code: string }) => (
    e: TouchEvent | MouseEvent
  ) => {
    e.preventDefault();
    onKeyDownHandler({ code } as KeyboardEvent);
  };

  useEffect(() => {
    // keyboard support
    document.addEventListener("keydown", onKeyDownHandler);
    return () => document.removeEventListener("keydown", onKeyDownHandler);
  });

  const hasTouch = isTouchSupported();

  // long press and release doesn't work in mobile
  // the way we expect, we need to add both events
  const handlerClickWithCode = (code: string) => ({
    onMouseUp: hasTouch ? null : onClickHandler({ code }),
    onTouchEnd: hasTouch ? onClickHandler({ code }) : null
  });

  // long press in the Clear button is even more
  // complex due to the fancy "long press to clear all"
  const clearDigitHandlers = {
    onMouseDown: hasTouch ? null : startClearTimer,
    onMouseUp: hasTouch
      ? null
      : e => {
          stopClearTimer(e);
          onClickHandler({ code: "Backspace" })(e);
        },
    onTouchStart: hasTouch
      ? (e: React.TouchEvent<HTMLLIElement>) => startClearTimer(e)
      : null,
    onTouchEnd: hasTouch
      ? e => {
          stopClearTimer(e);
          onClickHandler({ code: "Backspace" })(e);
        }
      : null
  };

  const child = (
    <View onClick={onClickOnViewHandler}>
      <div>
        <div>
          {!isEmpty(alt) && <Alt>{alt}</Alt>}
          <CloseButton onClick={onCloseClickHandler}>
            <svg viewBox="0 0 24 24">
              <g>
                <path
                  fill="white"
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                />
                <path fill="none" d="M0 0h24v24H0z" />
              </g>
            </svg>
          </CloseButton>
        </div>
        <Value currency={currency} numChars={value.length} maxChars={maxChars}>
          <span>{value}</span>
        </Value>
        <Digits>
          <Digit digit="Digit1" {...handlerClickWithCode("Digit1")}>
            1
          </Digit>
          <Digit digit="Digit2" {...handlerClickWithCode("Digit2")}>
            2
          </Digit>
          <Digit digit="Digit3" {...handlerClickWithCode("Digit3")}>
            3
          </Digit>
          <Digit digit="Digit4" {...handlerClickWithCode("Digit4")}>
            4
          </Digit>
          <Digit digit="Digit5" {...handlerClickWithCode("Digit5")}>
            5
          </Digit>
          <Digit digit="Digit6" {...handlerClickWithCode("Digit6")}>
            6
          </Digit>
          <Digit digit="Digit7" {...handlerClickWithCode("Digit7")}>
            7
          </Digit>
          <Digit digit="Digit8" {...handlerClickWithCode("Digit8")}>
            8
          </Digit>
          <Digit digit="Digit9" {...handlerClickWithCode("Digit9")}>
            9
          </Digit>
          <ClearDigit digit="Backspace" {...clearDigitHandlers}>
            <svg viewBox="0 0 23 18">
              <path
                fill="#FFFFFF"
                d="M7.07 14.983c.093.096.188.19.338.336.076.078.152.152.257.253.03.027.036.033.103.105l.05.051a2.817 2.817 0 0 0 1.546.79c.326.055.655.084.996.088a41.633 41.633 0 0 0 .482.01h5.891V18l-.002-.692v-.693h.595c.13 0 .239-.002.491-.009.332-.004.662-.033.988-.087.29-.049.572-.142.836-.276.262-.133.5-.306.706-.512.208-.21.383-.45.514-.704.134-.267.228-.552.278-.84.053-.326.082-.656.086-.998.005-.18.008-.335.008-.483V5.59v-.287l-.001-.126a19.787 19.787 0 0 0-.007-.377 6.645 6.645 0 0 0-.085-.98A2.822 2.822 0 0 0 18.8 1.48a6.04 6.04 0 0 0-.994-.087c-.242-.007-.35-.01-.48-.01H10.84c-.086 0-.176.002-.285.004l-.212.006a5.964 5.964 0 0 0-.98.087 2.86 2.86 0 0 0-1.56.804c-.075.079-.104.11-.13.134a9.5 9.5 0 0 0-.278.273c-.152.148-.236.232-.333.331L4.727 5.36a.867.867 0 0 0-.07.063l-.054.054a8.792 8.792 0 0 0-.225.224L2.308 7.77l-.043.043-.047.047-.102.103a3.297 3.297 0 0 0-.137.142 5 5 0 0 0-.433.476l-.05.057a.427.427 0 0 0-.076.103.962.962 0 0 0 0 .518.522.522 0 0 0 .085.119c.163.197.301.349.453.5L2 9.917l.04.04a12.838 12.838 0 0 0 .18.183l.096.096 2.059 2.062.202.196.134.134c.024.02.047.04.069.064l2.29 2.29zm3.915-9.427a.598.598 0 0 0-.921-.096.595.595 0 0 0-.003.838l-.011.011 2.693 2.687-2.59 2.591a.6.6 0 0 0-.1.925.597.597 0 0 0 .84.004l.009.01 2.686-2.687 2.69 2.683.006-.007a.595.595 0 1 0 .755-.915l-2.608-2.604 2.692-2.691-.007-.007a.597.597 0 1 0-.911-.764l-2.619 2.619-2.6-2.597zm-7.215 8.09l-.165-.163-.205-.2-2.062-2.066a12.899 12.899 0 0 1-.2-.2 13.953 13.953 0 0 1-.086-.088l-.075-.076a7.317 7.317 0 0 1-.495-.542 1.906 1.906 0 0 1-.343-.524L.113 9.72a2.346 2.346 0 0 1 0-1.44l.024-.063c.084-.193.2-.37.345-.523.149-.187.31-.364.48-.531a4.76 4.76 0 0 1 .177-.182l.093-.094.052-.053.05-.05 2.053-2.05c.064-.068.122-.125.212-.213l.085-.084a8.94 8.94 0 0 1 .092-.09l2.3-2.296c.1-.103.192-.194.34-.338.095-.098.194-.195.305-.299A4.207 4.207 0 0 1 9.131.116c.397-.068.8-.104 1.189-.107A42.704 42.704 0 0 1 10.84 0h6.486c.148 0 .264.002.504.01.405.002.808.037 1.203.106.43.071.847.209 1.235.407a4.215 4.215 0 0 1 1.831 1.83c.195.388.331.803.406 1.237.064.394.1.793.104 1.18a19.848 19.848 0 0 1 .009.524v7.412c0 .163-.003.332-.009.512-.005.4-.04.798-.105 1.198a4.45 4.45 0 0 1-.409 1.239c-.2.387-.458.742-.767 1.053-.31.31-.668.57-1.059.769a4.27 4.27 0 0 1-1.237.407 7.862 7.862 0 0 1-1.187.107c-.255.007-.371.009-.519.009h-.595v-1.385l.002.693V18h-5.891a13.433 13.433 0 0 1-.51-.01c-.4-.004-.8-.04-1.196-.106a4.297 4.297 0 0 1-1.24-.407 4.166 4.166 0 0 1-1.134-.849l-.041-.043a11.315 11.315 0 0 1-.292-.286 19.127 19.127 0 0 1-.344-.343l-2.238-2.237-.077-.073z"
              />
            </svg>
          </ClearDigit>
          <Digit digit="Digit0" {...handlerClickWithCode("Digit0")}>
            0
          </Digit>
          <DigitEnabled digit="Enter" {...handlerClickWithCode("Enter")}>
            <svg viewBox="0 0 24 24">
              <path
                fill="white"
                d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"
              />
            </svg>
          </DigitEnabled>
        </Digits>
      </div>
    </View>
  );

  // This component is rendered on top of the keypad
  return ReactDOM.createPortal(child, container);
}

const View = styled.div`
  * {
    user-select: none;
    pointer-events: all;
  }

  position: fixed;
  z-index: 999;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  align-items: flex-end;

  pointer-events: all;
  background-color: rgba(0, 0, 0, 0.4);

  @media (min-width: 640px) {
    /* desktop */
    background-color: rgba(0, 0, 0, 0.9);
    align-items: center;
    justify-content: center;
  }

  > div {
    display: flex;
    flex: 1 0;
    flex-direction: column;
    justify-content: flex-end;

    font-family: proxima-nova, sans-serif;
    font-size: 1em;

    padding: 1em 1.25em;
    background-color: rgba(0, 0, 0, 1);
    box-shadow: 0px -5px 20px 0px black;

    > div:nth-child(1) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    @media (min-width: 640px) {
      padding: 1.25em;
      font-size: 0.8em;
      flex: 0 1 32em;
    }

    @media (orientation: portrait) {
      /* extra bottom padding to avoid the iOS status bar */
      padding: 1.25em 2em 4em;
    }

    ${iPadPortraitOnly(css`
      padding-left: 4em;
      padding-right: 4em;
    `)}
  }
`;

export const Alt = styled.h1`
  margin: 0;
  padding: 0;
  font-size: 1em;
  font-weight: normal;
  color: white;
  text-align: left;
`;

export const Value = styled.div<{
  currency: string;
  numChars: number;
  maxChars: number;
}>`
  display: flex;
  /*
     determine the font size based on the number of characters
     but setting a minimum of 8 chars before starting the calculation
  */
  height: 2.25em;
  color: white;
  margin: 0.4em 0;
  justify-content: space-between;
  align-items: center;
  letter-spacing: 0.03em;
  background-color: #1f1f1f;
  border-radius: 0.1em;
  padding: 0.3em 0.9em;
  position: relative;

  &:after {
    content: "${({ currency }) => currency}";
    font-size: 1.2em;
  }
  span {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    font-size: 1.675em;
    @media (orientation: portrait){
      font-size: ${({ numChars, maxChars }) =>
        1.4 * (maxChars / Math.max(numChars, 8))}em;
    }
  }
`;

export const CloseButton = styled.div`
  pointer-events: all;
  font-size: 1em;
  color: #999;
  margin-left: auto;
  cursor: pointer;
  transition: opacity 300ms ease-in-out;

  &:hover {
    opacity: 0.5;
  }

  svg {
    width: 1.7em;
    height: 1em;
  }
`;

export const Digits = styled.ul`
  /* ie11 only - start */
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  > li {
    flex: 1 0 30.333%;
  }
  /* ie11 only - end */

  @supports (display: grid) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
  }

  list-style: none;
  margin: 0.5em 0;
  padding: 0;
  font-weight: bold;
`;

export const DigitEnabled = styled.li`
  pointer-events: all;
  padding: 0.3em 0;
  font-size: 1.475em;
  font-weight: normal;
  transform-origin: center;
  border-radius: 0.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: black;
  margin: 0.1em;

  &:active {
    background-color: grey;
  }

  &:nth-child(10),
  &:nth-child(12) {
    color: white;
    svg {
      width: 1em;
      height: 1em;
      path {
        fill: white;
      }
    }
  }

  /* clear button */
  &:nth-child(10) {
    background-color: transparent;
  }

  /* done button */
  &:nth-child(12) {
    font-size: 1.2em;
    background-color: #7ed321;
  }

  &:nth-child(10):active {
    background-color: rgba(255, 66, 66, 0.9);
  }

  &:nth-child(12):active {
    opacity: 0.7;
  }

  @media (max-width: 920px) and (orientation: landscape) {
    padding: 0.2em 0;
  }

  @media (min-width: 920px) and (orientation: landscape) {
    font-size: 1.875em;
  }
`;

export const DigitDisabled = styled(DigitEnabled)`
  pointer-events: none;
  background-color: #ccc;
  color: grey;

  &:nth-child(10),
  &:nth-child(12) {
    color: grey;
    svg path {
      fill: grey;
    }
  }

  &:nth-child(10),
  &:nth-child(12) {
    &:active {
      background-color: initial;
    }
  }
`;
